import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: 120,
  },
}));

const initialState = {
  enabled: true,
  whatsappId: "",
  queueId: "",
  strategy: "round_robin",
  keepOwnerOnReopen: true,
  members: [],
};

const strategyOptions = ["round_robin", "least_open", "random"];

const DistributionRuleModal = ({ open, onClose, rule, onSaved }) => {
  const classes = useStyles();

  const [form, setForm] = useState(initialState);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);

  const { findAll: findAllQueues } = useQueues();
  const { whatsApps } = useWhatsApps();
  const [queues, setQueues] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const list = await findAllQueues();
        setQueues(list);
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;

    (async () => {
      setLoadingUsers(true);
      try {
        const { data } = await api.get("/users/");
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setForm(initialState);
      setSelectedUsers([]);
      return;
    }

    if (rule) {
      setForm({
        enabled: rule.enabled,
        whatsappId: rule.whatsappId || "",
        queueId: rule.queueId || "",
        strategy: rule.strategy,
        keepOwnerOnReopen: rule.keepOwnerOnReopen,
        members: rule.members || [],
      });
      setSelectedUsers(
        (rule.members || [])
          .filter((m) => m.active !== false)
          .map((m) => m.user)
          .filter(Boolean),
      );
    } else {
      setForm(initialState);
      setSelectedUsers([]);
    }
  }, [rule, open]);

  const handleClose = () => {
    onClose();
    setForm(initialState);
    setSelectedUsers([]);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      enabled: form.enabled,
      whatsappId: form.whatsappId || null,
      queueId: form.queueId || null,
      strategy: form.strategy,
      keepOwnerOnReopen: form.keepOwnerOnReopen,
      members: selectedUsers.map((u) => ({ userId: u.id, active: true })),
    };

    try {
      if (rule) {
        await api.put(`/distribution-rules/${rule.id}`, payload);
      } else {
        await api.post("/distribution-rules", payload);
      }
      toast.success(i18n.t("distributionRuleModal.success"));
      if (onSaved) onSaved();
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        {rule
          ? i18n.t("distributionRuleModal.title.edit")
          : i18n.t("distributionRuleModal.title.add")}
      </DialogTitle>
      <DialogContent dividers>
        <FormControl
          variant="outlined"
          className={classes.formControl}
          fullWidth
        >
          <InputLabel>
            {i18n.t("distributionRuleModal.form.whatsapp")}
          </InputLabel>
          <Select
            value={form.whatsappId}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, whatsappId: e.target.value }))
            }
            label={i18n.t("distributionRuleModal.form.whatsapp")}
          >
            <MenuItem value="">
              {i18n.t("distributionRuleModal.form.allConnections")}
            </MenuItem>
            {whatsApps.map((whatsapp) => (
              <MenuItem key={whatsapp.id} value={whatsapp.id}>
                {whatsapp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          className={classes.formControl}
          fullWidth
        >
          <InputLabel>{i18n.t("distributionRuleModal.form.queue")}</InputLabel>
          <Select
            value={form.queueId}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, queueId: e.target.value }))
            }
            label={i18n.t("distributionRuleModal.form.queue")}
          >
            <MenuItem value="">
              {i18n.t("distributionRuleModal.form.allQueues")}
            </MenuItem>
            {queues.map((queue) => (
              <MenuItem key={queue.id} value={queue.id}>
                {queue.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          className={classes.formControl}
          fullWidth
        >
          <InputLabel>
            {i18n.t("distributionRuleModal.form.strategy")}
          </InputLabel>
          <Select
            value={form.strategy}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, strategy: e.target.value }))
            }
            label={i18n.t("distributionRuleModal.form.strategy")}
          >
            {strategyOptions.map((strategy) => (
              <MenuItem key={strategy} value={strategy}>
                {i18n.t(`distributionRuleModal.strategies.${strategy}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          className={classes.formControl}
          options={users}
          value={selectedUsers}
          getOptionLabel={(option) => option.name || ""}
          getOptionSelected={(option, value) => option.id === value.id}
          loading={loadingUsers}
          onChange={(e, newValue) => setSelectedUsers(newValue)}
          noOptionsText={i18n.t("distributionRuleModal.form.noUsers")}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={i18n.t("distributionRuleModal.form.members")}
              helperText={i18n.t("distributionRuleModal.form.membersHelper")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <FormControlLabel
          className={classes.formControl}
          control={
            <Switch
              checked={form.keepOwnerOnReopen}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  keepOwnerOnReopen: e.target.checked,
                }))
              }
              color="primary"
            />
          }
          label={i18n.t("distributionRuleModal.form.keepOwnerOnReopen")}
        />

        <FormControlLabel
          className={classes.formControl}
          control={
            <Switch
              checked={form.enabled}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, enabled: e.target.checked }))
              }
              color="primary"
            />
          }
          label={i18n.t("distributionRuleModal.form.enabled")}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          disabled={saving}
          variant="outlined"
        >
          {i18n.t("distributionRuleModal.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={saving}
          variant="contained"
        >
          {rule
            ? i18n.t("distributionRuleModal.buttons.okEdit")
            : i18n.t("distributionRuleModal.buttons.okAdd")}
          {saving && <CircularProgress size={20} style={{ marginLeft: 8 }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DistributionRuleModal;
